import React from "react";
import { useProjectSource, type Source } from "../../../../../../../../api/useSources";
import { isNonEmptyArray } from "diagnostics-lib/array";
import type { NonEmptyArray } from "diagnostics-lib/array";
import useSelectedQuantities, {
  type SelectedQuantity,
} from "../../../../../../useSelectedQuantities";
import Loading from "@ui/Loading";
import BlankStateSymptoms from "../../../../../../Symptoms/components/BlankStateSymptoms";
import { useNavigate, useOutletContext } from "react-router";
import useSelectedAnalyses from "../../../../../../Analysis/useSelectedAnalyses";
import QuantitySelector from "../../../FaultEvent/components/Tabs/components/QuantitySelector";
import { QuantitySelectorType } from "../../../../../../QuantitySelector";
import BlankStateV2 from "@ui/BlankStateV2";
import ScreenGraph from "@ui/Picto/ScreenGraph";
import t from "../../../../../../../../i18n";
import QuantityLayout from "../../../../../../Chart/QuantityLayout";
import { useSymptomChartType } from "../../../../../../Symptoms/hook/useSymptomChartType";
import type { Project } from "../../../../../../../../api/useProjects";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import SymptomEvolution from "./SymptomEvolution";
import { css } from "@linaria/core";
import {
  FAULT_EVENT_HEADER_HEIGHT_CSS_VARIABLE,
  FAULT_EVENT_NAVIGATION_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "@ui/Header/useHeaderHeight";
import type { Quantity } from "../../../../../../../../api/useQuantities";
import type { FaultEventOutletContext } from "../../../FaultEvent";
import { toPath } from "../../../../../../../../router/helpers/path";
import type { Event } from "../../../../../../../../api/useEvents";
import useActiveAnalysesWithDisplayFilters from "../../../../../../../../api/useActiveAnalysesWithDisplayFilters";

interface ProjectSymptomsProps {
  event: Event;
  project: Project;
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
}

const ProjectSymptoms = ({ project, event, source, selectedQuantities }: ProjectSymptomsProps) => {
  const navigate = useNavigate();
  const projectId = project.id;
  const { displayFilterRemovesAllActiveAnalyses, activeAnalyses } =
    useActiveAnalysesWithDisplayFilters(project.id);
  const { selectedAnalyses } = useSelectedAnalyses(projectId, activeAnalyses);

  const [_, _setSelectedQuantities] = useSelectedQuantities(project.id);
  const [selectedChartType, setSelectedChartType] = useSymptomChartType({
    scope: projectId,
    defaultValue: "amplitude",
  });
  const [period] = useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const setSelectedQuantities = React.useCallback(
    (newQuantities: Quantity[]) => {
      if (
        newQuantities.length > 1 &&
        selectedQuantities.length < 2 &&
        selectedChartType !== "normalizedGap"
      ) {
        setSelectedChartType("normalizedGap");
      }

      _setSelectedQuantities(newQuantities);
    },
    [_setSelectedQuantities, selectedChartType, selectedQuantities.length, setSelectedChartType],
  );

  React.useEffect(() => {
    if (selectedQuantities.length > 1 && selectedChartType !== "normalizedGap") {
      setSelectedChartType("normalizedGap");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quantitySelector = React.useMemo(
    () => (
      <QuantitySelector
        event={event}
        project={project}
        onlyFirstDisplayed={selectedChartType !== "normalizedGap"}
        selectedQuantities={selectedQuantities}
        setSelectedQuantities={setSelectedQuantities}
        activeAnalyses={activeAnalyses}
        selectedAnalyses={selectedAnalyses}
        alertType={QuantitySelectorType.Symptoms}
        period={period}
        allAnalysesMode={false}
        page={QuantitySelectorType.Symptoms}
      />
    ),
    [
      event,
      project,
      selectedChartType,
      selectedQuantities,
      setSelectedQuantities,
      activeAnalyses,
      selectedAnalyses,
      period,
    ],
  );

  if ((!isNonEmptyArray(activeAnalyses) && !displayFilterRemovesAllActiveAnalyses) || !source)
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("common.empty.activeAnalyses.title")}
        subtitle={t("common.empty.activeAnalyses.description")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/measures", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("common.empty.activeAnalyses.action")}
      />
    );

  return (
    <QuantityLayout className={styledQuantityLayout} quantities={quantitySelector} compact>
      <SymptomEvolution
        project={project}
        source={source}
        activeAnalyses={activeAnalyses}
        selectedQuantities={selectedQuantities}
        selectedChartOption={selectedChartType}
        setSelectedChartOption={setSelectedChartType}
        event={event}
      />
    </QuantityLayout>
  );
};

export const SymptomTab = () => {
  const { project, event } = useOutletContext<FaultEventOutletContext>();
  if (!project) throw new Error("no project");
  if (!event) throw new Error("no event");

  const [selectedQuantities] = useSelectedQuantities(project.id);
  const source = useProjectSource(project);
  if (source === null) return <Loading />; // Will happen temporarily if source is deletable before project gets removed as well or if project is created before source get a chance to be synced

  if (!isNonEmptyArray(selectedQuantities)) return <BlankStateSymptoms />;

  return (
    <ProjectSymptoms
      project={project}
      event={event}
      source={source}
      selectedQuantities={selectedQuantities}
    />
  );
};

//#region CSS
const styledQuantityLayout = css`
  --dyn-min-height: calc(
    100vh - var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) - var(
        ${FAULT_EVENT_HEADER_HEIGHT_CSS_VARIABLE}
      ) - var(${FAULT_EVENT_NAVIGATION_HEIGHT_CSS_VARIABLE})
  );
  min-height: max(var(--dyn-min-height), 650px);
`;
//#endregion CSS
